import type { ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { onboardingPageClass } from '@/components/smartai/ComponentOnboarding/onboarding-layout';

export const coveragePageClass = onboardingPageClass;

export const coverageCardClass =
  'min-w-0 rounded-[14px] border border-[var(--inv-border)] bg-[var(--inv-surface)] p-3.5 shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))] max-md:p-3';

/** Summary metric cards — 2-up on phones, 3-up from tablet/laptop. */
export const coverageMetricsGridClass =
  'mb-4 grid w-full min-w-0 grid-cols-2 gap-2 min-[640px]:grid-cols-3 min-[640px]:gap-2.5';

/** Code-element breakdown — compact tiles, 5-up only on very wide panes. */
export const coverageElementGridClass =
  'mb-4 grid w-full min-w-0 grid-cols-2 gap-2 min-[640px]:grid-cols-3 min-[640px]:gap-2.5 min-[1500px]:grid-cols-5';

export type CoverageMode = 'lines' | 'inventory';

interface CoverageMetricCardProps {
  title: string;
  value: string;
  subtitle?: string;
  progress?: number;
  accent?: 'default' | 'success' | 'info' | 'danger';
  /** Shown top-right (e.g. delta badge). */
  trailing?: ReactNode;
}

const accentBorder: Record<NonNullable<CoverageMetricCardProps['accent']>, string> =
  {
    default: 'border-l-[var(--inv-accent)]',
    success: 'border-l-[#16a34a]',
    info: 'border-l-[#2563eb]',
    danger: 'border-l-[#d92d20]',
  };

export function CoverageMetricCard({
  title,
  value,
  subtitle,
  progress,
  accent = 'default',
  trailing,
}: CoverageMetricCardProps) {
  return (
    <div
      className={cn(
        'min-w-0 rounded-lg border border-solid border-[var(--inv-border)] border-l-[3px] bg-[var(--inv-surface)] px-3 py-2 shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]',
        accentBorder[accent],
      )}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-[var(--inv-text-muted)]">
            {title}
          </p>
          <p className="text-[1.0625rem] font-bold leading-none tabular-nums text-[var(--inv-text)]">
            {value}
          </p>
          {progress !== undefined ? (
            <Progress value={progress} showLabel={false} className="mt-1.5 h-1" />
          ) : null}
          {subtitle ? (
            <p className="mt-1 text-[0.6875rem] leading-snug text-[var(--inv-text-secondary)]">
              {subtitle}
            </p>
          ) : null}
        </div>
        {trailing ? (
          <div className="flex shrink-0 items-center self-center">{trailing}</div>
        ) : null}
      </div>
    </div>
  );
}

interface CoverageModeToolbarProps {
  mode: CoverageMode;
  onModeChange: (mode: CoverageMode) => void;
  isFetching: boolean;
  onRefresh: () => void;
}

export function CoverageModeToolbar({
  mode,
  onModeChange,
  isFetching,
  onRefresh,
}: CoverageModeToolbarProps) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h3 className="m-0 text-[0.875rem] font-semibold text-[var(--inv-text)]">
          Coverage calculation mode
        </h3>
        <p className="m-0 mt-0.5 text-[0.8125rem] text-[var(--inv-text-secondary)]">
          Choose how coverage is measured and displayed.
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {/* {isFetching ? (
          <span className="text-[0.8125rem] text-[var(--inv-text-muted)]">
            Refreshing…
          </span>
        ) : null} */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-lg"
          onClick={onRefresh}>
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
        <div
          className="inline-flex rounded-[10px] border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] p-0.5"
          role="group"
          aria-label="Coverage calculation mode">
          <Button
            type="button"
            variant="ghost"
            className={cn(
              'h-auto rounded-lg px-[0.85rem] py-1.5 text-[0.8125rem] shadow-none hover:!bg-[var(--inv-surface)]',
              mode === 'lines'
                ? 'bg-[var(--inv-surface)] text-[var(--inv-accent)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.06))] hover:!text-[var(--inv-accent)]'
                : 'bg-transparent text-[var(--inv-text-secondary)] hover:!text-[var(--inv-text)]',
            )}
            onClick={() => onModeChange('lines')}>
            Lines of code
          </Button>
          <Button
            type="button"
            variant="ghost"
            className={cn(
              'h-auto rounded-lg px-[0.85rem] py-1.5 text-[0.8125rem] shadow-none hover:!bg-[var(--inv-surface)]',
              mode === 'inventory'
                ? 'bg-[var(--inv-surface)] text-[var(--inv-accent)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.06))] hover:!text-[var(--inv-accent)]'
                : 'bg-transparent text-[var(--inv-text-secondary)] hover:!text-[var(--inv-text)]',
            )}
            onClick={() => onModeChange('inventory')}>
            Inventory
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CoverageModeExplanation({
  mode,
  children,
}: {
  mode: CoverageMode;
  children: ReactNode;
}) {
  return (
    <div className="mb-4 rounded-[10px] border border-solid border-[var(--inv-border-soft)] border-l-4 border-l-[var(--inv-accent)] bg-[var(--inv-surface-muted)] px-3.5 py-2.5 text-[0.8125rem] leading-relaxed text-[var(--inv-text-secondary)]">
      <strong className="text-[var(--inv-text)]">
        {mode === 'lines' ? 'Lines of code mode:' : 'Inventory mode:'}
      </strong>{' '}
      {children}
    </div>
  );
}

export function CoverageStateMessage({
  variant,
  children,
}: {
  variant: 'empty' | 'error' | 'loading';
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-[10px] border border-solid px-5 py-8 text-center text-[0.875rem]',
        variant === 'error'
          ? 'border-[#fecaca] bg-[#fef2f2] text-[#b42318]'
          : 'border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] text-[var(--inv-text-secondary)]',
      )}>
      {children}
    </div>
  );
}

export function PollerBadges({
  lastPolled,
  nextPoll,
}: {
  lastPolled: string;
  nextPoll: string;
}) {
  return (
    <div className="mb-3 flex flex-wrap gap-2">
      <span className="inline-flex items-center rounded-md border border-solid border-[#bbf7d0] bg-[#f0fdf4] px-2 py-0.5 text-[0.6875rem] font-medium text-[#15803d]">
        Last polled: {new Date(`${lastPolled}Z`).toLocaleString()}
      </span>
      <span className="inline-flex items-center rounded-md border border-solid border-[#bbf7d0] bg-[#f0fdf4] px-2 py-0.5 text-[0.6875rem] font-medium text-[#15803d]">
        Next poll: {new Date(`${nextPoll}Z`).toLocaleString()}
      </span>
    </div>
  );
}

export function coveragePctClass(pct: number): string {
  if (pct >= 80) return 'font-semibold text-[#16a34a]';
  if (pct >= 50) return 'font-semibold text-[#d97706]';
  return 'font-semibold text-[#d92d20]';
}
